import React, { useMemo, useState } from "react";
import { RowAccessEditor } from "./RowAccessEditor";

const TABS = [["new", "New staff"], ["update", "Updates"], ["unchanged", "Unchanged"], ["held", "Held for your decision"], ["former", "Former staff"], ["problem", "Duplicates & invalid"]];
const Field = ({ k, v }) => <span className="text-[12.5px] text-slate-500 mr-3"><b className="text-slate-700">{k}:</b> {v}</span>;

const initialDecisions = (items) => Object.fromEntries(items.map((i) => [i.row, {
  action: i.kind === "new" ? "create" : i.kind === "update" ? "update" : i.kind === "held" ? "hold" : "skip",
  access_role: i.kind === "new" || i.kind === "held" ? i.suggested_role : i.existing?.access_role,
  assigned_homes: i.suggested_homes || [], change_access: false, confirmed: false, merge_user_id: i.suggestions?.[0]?.user_id || null, activate: true,
}]));

export const ImportReviewStep = ({ preview, onBack, onCommit, busy }) => {
  const { items, summary, known_homes: homes, roles } = preview;
  const [tab, setTab] = useState(summary.new ? "new" : summary.held ? "held" : "update");
  const [dec, setDec] = useState(() => initialDecisions(items));
  const upd = (row, patch) => setDec((d) => ({ ...d, [row]: { ...d[row], ...patch } }));

  const visible = items.filter((i) => (tab === "problem" ? i.kind === "duplicate" || i.kind === "invalid" : i.kind === tab));
  const pending = useMemo(() => items.filter((i) => {
    const d = dec[i.row];
    if (i.kind === "new") return d.action === "create" && (!d.confirmed || !d.access_role);
    if (i.kind === "held") return d.action === "create" || d.action === "merge" ? !d.confirmed : false;
    if (i.kind === "update") return d.change_access && !d.confirmed;
    return false;
  }), [items, dec]);
  const willSave = items.filter((i) => ["create", "update", "merge"].includes(dec[i.row].action) && !(i.kind === "held" && dec[i.row].action === "hold")).length;

  const submit = () => onCommit(items.map((i) => {
    const d = dec[i.row];
    const action = i.kind === "held" && d.action === "hold" ? "skip" : d.action;
    return { row: i.row, action, merge_user_id: d.merge_user_id, access_role: d.access_role || null, assigned_homes: d.assigned_homes, change_access: d.change_access, activate: d.activate };
  }));

  return (
    <div className="space-y-4" data-testid="import-review-step">
      <div className="flex flex-wrap gap-2" data-testid="review-tabs">
        {TABS.map(([k, label]) => {
          const n = k === "problem" ? summary.duplicate + summary.invalid : summary[k];
          return <button key={k} onClick={() => setTab(k)} className={`text-xs px-3 py-1.5 rounded-full border ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`} data-testid={`review-tab-${k}`}>{label} <b>{n}</b></button>;
        })}
      </div>

      <div className="space-y-2">
        {!visible.length && <div className="nv-card p-6 text-sm text-slate-500 text-center">Nothing in this group.</div>}
        {visible.map((i) => {
          const d = dec[i.row];
          const p = i.proposed;
          return (
            <div key={i.row} className="nv-card p-4 space-y-2" data-testid={`review-row-${i.row}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{p.full_name || "(no name)"} <span className="font-normal text-slate-500">· {i.email || "no email"} · row {i.row}</span></p>
                  <div className="mt-1 flex flex-wrap">
                    {p.job_title && <Field k="Job title" v={p.job_title} />}
                    {p.staff_classification && <Field k="Classification" v={p.staff_classification} />}
                    {p.home && <Field k="Home in file" v={p.home} />}
                    {p.gender && <Field k="Gender" v={p.gender} />}
                    {p.phone && <Field k="Phone" v={p.phone} />}
                    {p.access_role && <Field k="Role in file" v={`${p.access_role} (suggestion only)`} />}
                  </div>
                  {i.issues.map((msg) => <p key={msg} className="text-[12.5px] text-amber-700 mt-1" data-testid={`review-issue-${i.row}`}>{msg}</p>)}
                </div>
                {i.kind === "new" && (
                  <label className="text-xs inline-flex items-center gap-1.5"><input type="checkbox" checked={d.action === "skip"} onChange={(e) => upd(i.row, { action: e.target.checked ? "skip" : "create" })} data-testid={`review-skip-${i.row}`} /> Skip this person</label>
                )}
              </div>

              {i.kind === "update" && (
                <div className="text-xs space-y-1" data-testid={`review-diffs-${i.row}`}>
                  <p className="text-slate-500">Matched by {i.match} → <b>{i.existing.full_name}</b> ({i.existing.email}) · current role {i.existing.access_role} · homes {(i.existing.assigned_homes || []).join(", ") || i.existing.assigned_home || "—"}</p>
                  <table className="text-xs"><tbody>{Object.entries(i.diffs).map(([f, dd]) => <tr key={f}><td className="pr-3 font-medium text-slate-700">{f}</td><td className="pr-3 text-slate-400 line-through">{dd.current || "(blank)"}</td><td className="text-emerald-700">{dd.proposed}</td></tr>)}</tbody></table>
                  <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={d.change_access} onChange={(e) => upd(i.row, { change_access: e.target.checked, confirmed: false })} data-testid={`review-change-access-${i.row}`} /> Also change role / home access (otherwise current permissions are preserved)</label>
                  {d.change_access && <RowAccessEditor value={d} onChange={(v) => upd(i.row, v)} roles={roles} homes={homes} row={i.row} />}
                </div>
              )}

              {i.kind === "former" && (
                <div className="text-xs border border-rose-200 bg-rose-50/60 rounded p-3 text-rose-900" data-testid={`review-former-${i.row}`}>
                  Matched <b>{i.existing.full_name}</b> ({i.existing.email}) · marked Former Staff {i.existing.former_since ? `on ${String(i.existing.former_since).slice(0, 10)}` : ""}. Held — nothing will be saved for this row.
                </div>
              )}

              {i.kind === "held" && (
                <div className="text-xs space-y-2 border border-amber-200 bg-amber-50/50 rounded p-3" data-testid={`review-held-${i.row}`}>
                  <p className="font-medium text-amber-900">Possible existing person:</p>
                  {i.suggestions.map((s) => <p key={s.user_id} className="text-slate-700">Existing: <b>{s.full_name}</b> · {s.email} · {s.access_role} · {s.assigned_home || "—"}</p>)}
                  <p className="text-slate-700">Uploaded: <b>{p.full_name}</b> · {i.email}</p>
                  <div className="flex flex-wrap gap-3">
                    {[["hold", "Keep on hold (not saved)"], ["create", "Different person — add as new"], ["merge", "Same person — update the existing account"]].map(([v, l]) => (
                      <label key={v} className="inline-flex items-center gap-1.5"><input type="radio" name={`held-${i.row}`} checked={d.action === v} onChange={() => upd(i.row, { action: v, confirmed: false, change_access: v === "merge" ? false : d.change_access })} data-testid={`review-held-${v}-${i.row}`} /> {l}</label>
                    ))}
                  </div>
                  {d.action === "merge" && <p className="text-[12.5px] text-slate-500">Merging updates name/title/phone fields on <b>{i.suggestions[0].email}</b>. The uploaded email is NOT added as a login and no account is deleted.</p>}
                </div>
              )}

              {((i.kind === "new" && d.action === "create") || (i.kind === "held" && d.action === "create")) && (
                <div className="space-y-2">
                  <RowAccessEditor value={d} onChange={(v) => upd(i.row, { ...v, confirmed: false })} roles={roles} homes={homes} row={i.row} />
                  <div className="flex flex-wrap gap-4 text-xs">
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={d.activate} onChange={(e) => upd(i.row, { activate: e.target.checked })} data-testid={`review-activate-${i.row}`} /> Active account</label>
                    <label className="inline-flex items-center gap-1.5 font-medium text-slate-800"><input type="checkbox" checked={d.confirmed} onChange={(e) => upd(i.row, { confirmed: e.target.checked })} data-testid={`review-confirm-${i.row}`} /> I have reviewed this person's role and home access</label>
                  </div>
                </div>
              )}
              {((i.kind === "held" && d.action === "merge") || (i.kind === "update" && d.change_access)) && (
                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-800"><input type="checkbox" checked={d.confirmed} onChange={(e) => upd(i.row, { confirmed: e.target.checked })} data-testid={`review-confirm-${i.row}`} /> I approve this change</label>
              )}
            </div>
          );
        })}
      </div>

      <div className="nv-card p-4 flex flex-wrap items-center justify-between gap-3 sticky bottom-2" data-testid="review-footer">
        <p className="text-xs text-slate-600">
          <b>{willSave}</b> record(s) will be saved · {pending.length > 0 ? <span className="text-amber-700" data-testid="review-pending">{pending.length} still need your access review (rows {pending.map((i) => i.row).join(", ")})</span> : <span className="text-emerald-700">all access reviews complete</span>}
        </p>
        <div className="flex gap-2">
          <button onClick={onBack} className="px-4 py-2 text-sm rounded-md border border-slate-200" data-testid="review-back">Back</button>
          <button onClick={submit} disabled={busy || pending.length > 0 || willSave === 0} className="px-4 py-2 text-sm rounded-md bg-emerald-600 text-white disabled:opacity-50" data-testid="review-save">{busy ? "Saving…" : `Save ${willSave} record(s)`}</button>
        </div>
      </div>
    </div>
  );
};
